import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MessageCircle } from 'lucide-react';
import {
  WHATSAPP_CONNECTION_STATE_LABELS,
  type WhatsAppAccountDto,
  type WhatsAppHistoryDto,
  type WhatsAppStoredDto,
} from '@aimmo/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  useIsCompact,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { useAbility } from '../lib/abilities.js';
import { formatDateTime } from '../lib/format.js';
import { queryKeys, whatsappAccountQuery } from '../lib/queries.js';

/**
 * Comunicazioni → Impostazioni.
 *
 * Everything about a channel that is not the conversation itself: which number
 * is connected, what an import brought, and what can be removed. The inbox is a
 * place somebody sits all day, and a settings panel bolted to the top of it is
 * furniture in the way — this is where that furniture goes.
 *
 * Vertical tabs because the list grows: newsletter and campagne land beside
 * WhatsApp, and a horizontal strip would have to be rewritten to hold them.
 */
export function CommunicationsSettingsPage() {
  const ability = useAbility();
  const canManage = ability.can('manage', 'WhatsAppAccount');
  /*
   * A column of tabs beside the panel needs a viewport that can spare the
   * width; a phone cannot. It is the prop that changes rather than only the
   * classes, because the orientation is also what tells Radix which arrow keys
   * move between tabs — a row that answers up and down is a row that lies.
   */
  const compact = useIsCompact();

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Impostazioni comunicazioni"
        description="I canali collegati all’agenzia e i dati che hanno lasciato in AImmo."
      />

      <Tabs defaultValue="whatsapp" orientation={compact ? 'horizontal' : 'vertical'}>
        <TabsList aria-label="Canali">
          <TabsTrigger value="whatsapp">
            <MessageCircle aria-hidden />
            WhatsApp
          </TabsTrigger>
        </TabsList>

        <TabsContent value="whatsapp">
          {canManage ? (
            <WhatsAppSettings />
          ) : (
            <p className="text-body text-muted">
              Solo un responsabile può vedere e cambiare il collegamento del numero.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WhatsAppSettings() {
  // An import moving is worth seeing here too: this is the screen somebody
  // opens to find out how far it has got, and the shared query polls while one runs.
  const account = useQuery(whatsappAccountQuery);

  if (account.isLoading) return <Skeleton className="h-64" />;
  if (!account.data) return null;

  return (
    <div className="grid gap-4">
      <Connection account={account.data} />
      {account.data.history.mode === 'full' && <History history={account.data.history} />}
      {account.data.stored.messages > 0 && <StoredData stored={account.data.stored} />}
    </div>
  );
}

/** The number itself: which one, in what state, since when. */
function Connection({ account }: { account: WhatsAppAccountDto }) {
  const queryClient = useQueryClient();

  const disconnect = useMutation({
    mutationFn: async () => assertOk(await api.whatsapp.disconnect({ body: {} })).body,
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.whatsapp.account, result);
      toast.success('Numero scollegato. Le conversazioni restano.');
    },
    onError: () => toast.error('Non è stato possibile scollegare il numero.'),
  });

  // Further than disconnecting: the connection itself goes, so the next one
  // starts from the QR and from the history choice again. The conversations
  // stay — removing them is the "Dati in AImmo" card's decision, not this one.
  const remove = useMutation({
    mutationFn: async () => assertOk(await api.whatsapp.remove({})).body,
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.whatsapp.account, result);
      toast.success('Collegamento rimosso. Le conversazioni restano.');
    },
    onError: () => toast.error('Non è stato possibile rimuovere il collegamento.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Numero collegato</CardTitle>
        <CardDescription>
          Il numero dell’agenzia da cui partono e arrivano le conversazioni.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <dl className="grid gap-2">
          <Row label="Stato">
            {account.state ? (
              <Badge tone={account.state === 'connected' ? 'success' : 'outline'}>
                {WHATSAPP_CONNECTION_STATE_LABELS[account.state]}
              </Badge>
            ) : (
              <span className="text-muted">Nessun collegamento</span>
            )}
          </Row>
          <Row label="Numero">{account.phoneNumber ?? '—'}</Row>
          <Row label="Collegato dal">
            {account.connectedAt ? formatDateTime(account.connectedAt) : '—'}
          </Row>
        </dl>

        {account.state && (
          <div className="flex flex-wrap gap-2">
            {account.state === 'connected' && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="subtle" disabled={disconnect.isPending}>
                    {disconnect.isPending && <Loader2 className="size-4 animate-spin" />}
                    Scollega il numero
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Scollegare il numero?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Smetterete di ricevere e inviare messaggi finché non ripetete la scansione. Le
                      conversazioni già in AImmo restano, e riscansionando riprendono da dove erano.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={() => disconnect.mutate()}>
                      Scollega
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" disabled={remove.isPending}>
                  {remove.isPending && <Loader2 className="size-4 animate-spin" />}
                  Rimuovi il collegamento
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Rimuovere il collegamento?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Il numero viene scollegato e il collegamento eliminato: per usarlo di nuovo
                    servirà una nuova scansione, e potrai scegliere di nuovo se importare lo
                    storico. Le conversazioni già in AImmo restano.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove.mutate()}>Rimuovi</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What an import brought, while it is bringing it and after (M8.1).
 *
 * Two things it must say and one it must offer. The count, because an import
 * runs for minutes and a screen that says nothing looks broken. What it could
 * not bring, because the attachments on old messages are references that
 * expired long before we saw them, and a thread quietly missing a photograph is
 * worse than one that admits it. And the way out: the copy can be withdrawn,
 * which is what `source` was added for.
 */
function History({ history }: { history: WhatsAppHistoryDto }) {
  const queryClient = useQueryClient();
  const running = history.state === 'running';

  const remove = useMutation({
    mutationFn: async () => assertOk(await api.whatsapp.removeHistory({})).body,
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.whatsapp.account, result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.whatsapp.conversations });
      toast.success('Le conversazioni importate sono state rimosse.');
    },
    onError: () => toast.error('Non è stato possibile rimuovere le conversazioni importate.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{running ? 'Importazione in corso' : 'Storico importato'}</CardTitle>
        <CardDescription>
          {running
            ? 'Le conversazioni compaiono man mano che arrivano. Puoi lasciare la pagina.'
            : `${history.imported} messaggi copiati dal telefono${
                history.windowMonths ? ` (ultimi ${history.windowMonths} mesi)` : ''
              }.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {running && (
          /*
           * A count, not a bar. How much WhatsApp will hand over is not known
           * before it has handed it over, so a percentage would be a number we
           * invented — and a progress bar that never fills is worse than a
           * figure that keeps moving.
           */
          <p className="flex items-center gap-2 text-secondary text-muted">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {history.imported} messaggi finora.
          </p>
        )}

        {history.startedAt && !running && (
          <p className="text-secondary text-muted">
            Importato il {formatDateTime(history.startedAt)}.
          </p>
        )}

        {history.attachmentsMissing > 0 && (
          <p className="text-secondary text-muted">
            {history.attachmentsMissing} messaggi avevano un allegato che WhatsApp non conserva più:
            il testo c’è, il file no.
          </p>
        )}

        {!running && history.imported > 0 && (
          <div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="subtle" disabled={remove.isPending}>
                  {remove.isPending && <Loader2 className="size-4 animate-spin" />}
                  Rimuovi le conversazioni importate
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Rimuovere le conversazioni importate?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Vengono cancellati i {history.imported} messaggi copiati dal telefono. I
                    messaggi arrivati dopo il collegamento restano, e i contatti restano: se hai
                    lavorato con qualcuno di loro, quel contatto è tuo.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove.mutate()}>Rimuovi</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What WhatsApp has left in AImmo, and the way to be rid of it (M8.1).
 *
 * Disconnecting a number does not take its conversations with it — they are the
 * agency's history and not the number's. But the agency is the one entitled to
 * decide whether they stay, which they cannot do without being told they exist.
 */
function StoredData({ stored }: { stored: WhatsAppStoredDto }) {
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: async () => assertOk(await api.whatsapp.removeData({})).body,
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.whatsapp.account, result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.whatsapp.conversations });
      toast.success('Le conversazioni WhatsApp sono state eliminate.');
    },
    onError: () => toast.error('Non è stato possibile eliminare le conversazioni.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dati in AImmo</CardTitle>
        <CardDescription>
          {stored.conversations} {stored.conversations === 1 ? 'conversazione' : 'conversazioni'} e{' '}
          {stored.messages} {stored.messages === 1 ? 'messaggio' : 'messaggi'}, ricevuti e inviati.
          Scollegare il numero non li cancella.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="subtle" disabled={remove.isPending}>
              {remove.isPending && <Loader2 className="size-4 animate-spin" />}
              Elimina tutte le conversazioni
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminare le conversazioni WhatsApp?</AlertDialogTitle>
              <AlertDialogDescription>
                Vengono cancellati {stored.messages} messaggi e {stored.conversations}{' '}
                conversazioni, ricevuti e inviati. I contatti restano, e resta la loro cronologia:
                sapere che una persona è arrivata da WhatsApp è un fatto sul contatto, non una copia
                di quello che ha scritto. L’operazione non si annulla.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={() => remove.mutate()}>Elimina</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <dt className="w-32 shrink-0 text-secondary text-muted">{label}</dt>
      <dd className="text-body">{children}</dd>
    </div>
  );
}
